// Sound chip — visible affordance for arming audio. The plan-review pass
// rejected invisible cursor-unmute; an honest UI chip + first-input gesture
// (pointerdown / touchstart / keydown) is the contract.

import type { OverlayHandles } from '../overlays';

export type ArmCallback = () => Promise<void>;

export function bindSoundToggle(overlays: OverlayHandles, arm: ArmCallback): { armed: () => boolean } {
  const supported = typeof window !== 'undefined' && (typeof window.AudioContext !== 'undefined' || typeof (window as unknown as { webkitAudioContext?: unknown }).webkitAudioContext !== 'undefined');
  if (!supported) {
    overlays.setSoundState('unsupported');
    return { armed: () => false };
  }

  let armed = false;
  let arming = false;
  const onAttempt = async (): Promise<void> => {
    if (armed || arming) return;
    arming = true;
    try {
      await arm();
      armed = true;
      overlays.setSoundState('live');
    } catch (err) {
      console.warn('[herminator] audio arm failed:', err);
      overlays.setSoundState('off');
    } finally {
      arming = false;
    }
  };

  // Click on the chip itself
  const chip = document.querySelector<HTMLButtonElement>('.herm-chip--sound');
  chip?.addEventListener('click', () => { void onAttempt(); });

  // Any of these first-gesture events: pointerdown, touchstart, keydown
  const firstInputOpts = { once: true, capture: true } as const;
  const firstInputHandler = (): void => { void onAttempt(); };
  document.addEventListener('pointerdown', firstInputHandler, firstInputOpts);
  document.addEventListener('touchstart', firstInputHandler, firstInputOpts);
  document.addEventListener('keydown', firstInputHandler, firstInputOpts);

  return { armed: () => armed };
}
